export const SETTINGS_STORAGE_KEY = "passo:settings";
export const SETTINGS_SCHEMA_VERSION = 2;

export type MotionPreference = "system" | "full" | "reduced";

export type Settings = {
  readonly soundEnabled: boolean;
  readonly hapticsEnabled: boolean;
  readonly motion: MotionPreference;
  readonly discMarkersEnabled: boolean;
};

export const DEFAULT_SETTINGS: Settings = Object.freeze({
  soundEnabled: true,
  hapticsEnabled: true,
  motion: "system",
  discMarkersEnabled: false,
});

type SettingsEnvelope = {
  readonly version: typeof SETTINGS_SCHEMA_VERSION;
  readonly settings: Settings;
};

export type SettingsLoadResult =
  | { readonly kind: "loaded"; readonly settings: Settings }
  | {
      readonly kind: "default";
      readonly settings: Settings;
      readonly reason: SettingsFallbackReason;
    };

export type SettingsFallbackReason = "missing" | "unavailable" | "invalid" | "unsupported-version";

export type SettingsWriteResult = { readonly kind: "saved" } | { readonly kind: "unavailable" };

export type SettingsStore = {
  load(): Promise<SettingsLoadResult>;
  save(settings: Settings): Promise<SettingsWriteResult>;
  clear(): Promise<SettingsWriteResult>;
};

export function createWebSettingsStore(
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">,
): SettingsStore {
  return {
    load() {
      let serialized: string | null;
      try {
        serialized = storage.getItem(SETTINGS_STORAGE_KEY);
      } catch {
        return Promise.resolve(fallback("unavailable"));
      }

      if (serialized === null) {
        return Promise.resolve(fallback("missing"));
      }

      let value: unknown;
      try {
        value = JSON.parse(serialized) as unknown;
      } catch {
        return Promise.resolve(fallback("invalid"));
      }

      const migrated = migrateV1(value);
      if (migrated !== null) {
        return Promise.resolve({ kind: "loaded" as const, settings: migrated });
      }
      if (hasUnsupportedVersion(value)) {
        return Promise.resolve(fallback("unsupported-version"));
      }
      if (!isSettingsEnvelope(value)) {
        return Promise.resolve(fallback("invalid"));
      }

      return Promise.resolve({ kind: "loaded" as const, settings: value.settings });
    },

    save(settings) {
      if (!isSettings(settings)) {
        return Promise.reject(new TypeError("Settings must match the current schema."));
      }
      const envelope: SettingsEnvelope = { version: SETTINGS_SCHEMA_VERSION, settings };
      try {
        storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(envelope));
        return Promise.resolve({ kind: "saved" as const });
      } catch {
        return Promise.resolve({ kind: "unavailable" as const });
      }
    },

    clear() {
      try {
        storage.removeItem(SETTINGS_STORAGE_KEY);
        return Promise.resolve({ kind: "saved" as const });
      } catch {
        return Promise.resolve({ kind: "unavailable" as const });
      }
    },
  };
}

function fallback(reason: SettingsFallbackReason): SettingsLoadResult {
  return { kind: "default", settings: DEFAULT_SETTINGS, reason };
}

function isSettingsEnvelope(value: unknown): value is SettingsEnvelope {
  if (
    !isRecord(value) ||
    value["version"] !== SETTINGS_SCHEMA_VERSION ||
    Object.keys(value).length !== 2
  ) {
    return false;
  }

  return isSettings(value["settings"]);
}

function hasUnsupportedVersion(value: unknown): boolean {
  return (
    isRecord(value) &&
    "version" in value &&
    value["version"] !== 1 &&
    value["version"] !== SETTINGS_SCHEMA_VERSION
  );
}

function migrateV1(value: unknown): Settings | null {
  if (
    !isRecord(value) ||
    value["version"] !== 1 ||
    Object.keys(value).length !== 2 ||
    !isLegacySettings(value["settings"])
  ) {
    return null;
  }
  return Object.freeze({ ...value["settings"], discMarkersEnabled: false });
}

function isLegacySettings(value: unknown): value is Omit<Settings, "discMarkersEnabled"> {
  return (
    isRecord(value) &&
    typeof value["soundEnabled"] === "boolean" &&
    typeof value["hapticsEnabled"] === "boolean" &&
    (value["motion"] === "system" || value["motion"] === "full" || value["motion"] === "reduced") &&
    Object.keys(value).length === 3
  );
}

function isSettings(value: unknown): value is Settings {
  return (
    isRecord(value) &&
    typeof value["soundEnabled"] === "boolean" &&
    typeof value["hapticsEnabled"] === "boolean" &&
    typeof value["discMarkersEnabled"] === "boolean" &&
    (value["motion"] === "system" || value["motion"] === "full" || value["motion"] === "reduced") &&
    Object.keys(value).length === 4
  );
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
